import React, { useState, useEffect, useCallback, useMemo, FormEvent } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  IconButton,
  Grid,
  TablePagination,
  Collapse
} from '@mui/material';
import { HiOutlinePencil, HiOutlineCheck, HiOutlineTicket, HiOutlineX, HiChevronUp, HiChevronDown } from 'react-icons/hi';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import moment from 'moment-jalaali';
import 'dayjs/locale/fa';
import { faIR } from '@mui/x-date-pickers/locales';

interface Discount {
  id: number;
  code: string;
  discount: number;
  max_use_count: number;
  use_count: number;
  expire_at: number | null;
  status: 'active' | 'expired' | 'deactive';
}

type Order = 'asc' | 'desc';
type SortableKeys = 'expire_at' | 'status';

interface DiscountsTabProps {
  apiFetch: (url: string, options?: RequestInit) => Promise<any>;
  handleApiCall: (apiCall: Promise<any>, successMessage: string) => Promise<void>;
  showConfirmationModal: (message: string, onConfirm: () => void) => void;
  theme: string;
}

const DiscountsTab: React.FC<DiscountsTabProps> = ({ apiFetch, handleApiCall, showConfirmationModal, theme }) => {
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [openSections, setOpenSections] = useState({ addDiscount: true, discountList: true });
  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalDiscounts, setTotalDiscounts] = useState(0);

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<SortableKeys>('status');
  
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editedData, setEditedData] = useState<Partial<Discount>>({});
  const [newDiscountExpireTime, setNewDiscountExpireTime] = useState<dayjs.Dayjs | null>(null)

  const fetchDiscounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`/admin/discounts?page=${page + 1}&size=${rowsPerPage}`);
      setDiscounts(response.items || []);
      setTotalDiscounts(response.total || 0);
    } catch (err: any) {
      setError(`خطا در بارگذاری کد‌های تخفیف: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, page, rowsPerPage]);

  useEffect(() => {
    fetchDiscounts();
  }, [fetchDiscounts]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSortRequest = (property: SortableKeys) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  
  const sortedDiscounts = useMemo(() => {
    const comparator = (a: Discount, b: Discount): number => {
      let valA = a[orderBy];
      let valB = b[orderBy];
      if (orderBy === 'expire_at') {
        valA = valA ?? 0;
        valB = valB ?? 0;
      }
      if (valB < valA) return -1;
      if (valB > valA) return 1;
      return 0;
    };
    const stabilizedThis = discounts.map((el, index) => [el, index] as [Discount, number]);
    stabilizedThis.sort((a, b) => {
      const cmp = comparator(a[0], b[0]);
      if (cmp !== 0) return cmp;
      return a[1] - b[1];
    });
    if (order === 'desc') {
        return stabilizedThis.map(el => el[0]).reverse();
    }
    return stabilizedThis.map(el => el[0]);
  }, [discounts, order, orderBy]);

  const handleEditClick = (discount: Discount) => {
    setEditingRowId(discount.id);
    const editableDiscount = { ...discount, expire_at: discount.expire_at ? dayjs.unix(discount.expire_at).valueOf() : null };
    setEditedData(editableDiscount);
  };

  const handleCancelClick = () => {
    setEditingRowId(null);
    setEditedData({});
  };

  const handleSaveClick = async (id: number) => {
    const payload = { ...editedData, expire_at: editedData.expire_at ? dayjs(editedData.expire_at).unix() : null };
    await handleApiCall(
        apiFetch(`/admin/discounts/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
        'کد تخفیف با موفقیت ویرایش شد.'
    );
    setEditingRowId(null);
    setEditedData({});
    fetchDiscounts();
  };

  const handleEditChange = (field: keyof Discount, value: any) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddDiscount = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const newDiscount = {
        code: formData.get('code') as string,
        discount: Number(formData.get('percent')),
        max_use_count: Number(formData.get('max_use_count')),
        expire_at: newDiscountExpireTime ? newDiscountExpireTime.unix() : null,
        status: formData.get('status') as 'active' | 'deactive'
    };
    await handleApiCall(
        apiFetch(`/admin/discounts`, { method: 'POST', body: JSON.stringify(newDiscount) }),
        'کد تخفیف جدید با موفقیت ساخته شد.'
    );
    setNewDiscountExpireTime(null);
    form.reset();
    fetchDiscounts();
  };


  const formInputStyles = {
    '& .MuiInputBase-root': {
        height: '56px',
        backgroundColor: 'var(--input-background)',
        borderRadius: '8px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        border:' 1px solid var(--border-color)',
    },
    '& .MuiInputBase-input': {
        textAlign: 'right',
        color: 'var(--text-color)',
    },
    '& .MuiInputBase-input::placeholder': {
        textAlign: 'right',
        opacity: 0.8,
    },
    '& .MuiInputLabel-root': {
        transformOrigin: 'top right',
        right: 28,
        left: 'auto'
    }
  };


  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  
  const collapsibleHeaderSx = {
    p: 2,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fa" localeText={faIR.components.MuiLocalizationProvider.defaultProps.localeText}>
      <Paper className='management-card' sx={{ p:0 }}>
          <Box sx={collapsibleHeaderSx} onClick={() => toggleSection('addDiscount')}>
             <h3 className='tab-section-header' style={{border: 'none', margin: 0, padding: 0}}><HiOutlineTicket /> افزودن کد تخفیف جدید</h3>
             {openSections.addDiscount ? <HiChevronUp /> : <HiChevronDown />}
          </Box>
          <Collapse in={openSections.addDiscount}>
            <Box p={2} component="form" onSubmit={handleAddDiscount}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                    <TextField name="code" label="کد تخفیف" fullWidth required variant="outlined" sx={formInputStyles} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <TextField name="percent" label="درصد" type="number" fullWidth required variant="outlined" sx={formInputStyles} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <TextField name="max_use_count" label="تعداد مجاز" type="number" fullWidth required variant="outlined" sx={formInputStyles} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <DateTimePicker
                        label="تاریخ انقضا"
                        value={newDiscountExpireTime}
                        onChange={(newValue) => setNewDiscountExpireTime(newValue)}
                        renderInput={(params) => <TextField {...params} sx={{...formInputStyles, '& .MuiInputBase-input': {textAlign: 'center'}}} />}
                        DialogProps={{ PaperProps: { className: `time-picker-dialog ${theme}` } }}
                    />
                </Grid>
            
                <Grid item xs={12} sx={{textAlign: 'left'}}>
                  <Button type='submit' variant='contained'>افزودن</Button>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
      </Paper>

      <Paper className='management-card' sx={{ p:0, mt: 2 }}>
        <Box sx={collapsibleHeaderSx} onClick={() => toggleSection('discountList')}>
          <Typography variant='h5'>لیست کدهای تخفیف</Typography>
          {openSections.discountList ? <HiChevronUp /> : <HiChevronDown />}
        </Box>
        <Collapse in={openSections.discountList}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{width: '5%'}}>ردیف</TableCell>
                    <TableCell>کد</TableCell>
                    <TableCell align="center">درصد</TableCell>
                    <TableCell align="center">تعداد مجاز</TableCell>
                    <TableCell align="center">تعداد مصرف</TableCell>
                    <TableCell align="center" sortDirection={orderBy === 'expire_at' ? order : false}>
                      <TableSortLabel active={orderBy === 'expire_at'} direction={orderBy === 'expire_at' ? order : 'asc'} onClick={() => handleSortRequest('expire_at')}>
                        تاریخ انقضا
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">عملیات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedDiscounts.map((row, index) => {
                    const isEditing = editingRowId === row.id;
                    return (
                      <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell align="center">{(page * rowsPerPage) + index + 1}</TableCell>
                        <TableCell component="th" scope="row">
                          {isEditing ? <TextField size="small" value={editedData.code} onChange={(e) => handleEditChange('code', e.target.value)} /> : row.code}
                        </TableCell>
                        <TableCell align="center">{isEditing ? <TextField size="small" type="number" value={editedData.discount} onChange={(e) => handleEditChange('discount', Number(e.target.value))} /> : `${row.discount}%`}</TableCell>
                        <TableCell align="center">{isEditing ? <TextField size="small" type="number" value={editedData.max_use_count} onChange={(e) => handleEditChange('max_use_count', Number(e.target.value))} /> : row.max_use_count}</TableCell>
                        <TableCell align="center">{row.use_count}</TableCell>
                        <TableCell align="center">
                          {isEditing ? (
                              <DateTimePicker
                                  value={editedData.expire_at ? dayjs(editedData.expire_at) : null}
                                  onChange={(newValue) => handleEditChange('expire_at', newValue ? newValue.valueOf() : null)}
                                  renderInput={(params) => <TextField {...params} size="small" />}
                              />
                          ) : (
                              row.expire_at ? moment.unix(row.expire_at).format('jYYYY/jMM/jDD') : '—'
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {isEditing ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                              <IconButton size="small" color="success" onClick={() => handleSaveClick(row.id)}><HiOutlineCheck /></IconButton>
                              <IconButton size="small" color="default" onClick={handleCancelClick}><HiOutlineX /></IconButton>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                              <IconButton size="small" color="primary" onClick={() => handleEditClick(row)}><HiOutlinePencil /></IconButton>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalDiscounts}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="تعداد در صفحه:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} از ${count !== -1 ? count : `بیشتر از ${to}`}`}
            />
        </Collapse>
      </Paper>
    </LocalizationProvider>
  );
};

export default DiscountsTab;